import { BaseValidation } from '@/components/datamodel/types';

import { Organization } from './Organization';
import { User } from './User';

export interface DataModel {

    /**
     * Returns all attributes defined in the active data model, i.e. without additional attributes.
     */
    allAttributes: () => DataModelAttribute[];

    allCategories: (includeDeleted?: boolean) => DataModelCategory[];

    category: (category: string) => DataModelCategory | undefined;

    /**
     * Returns attributes list by category name
    */
    categoryAttributes: (category: string) => DataModelAttribute[];

    hasLayout: (layoutName: string) => boolean;

    /**
     * Returns the layout based on the layout mapping and the items category,
     * if an item is given.
     */
    //eslint-disable-next-line
    layout: (layoutName: string, item?: any) => DataModelLayout;

    /**
     * Asset layouts are declared with the `asset_layout` DSL on the Ruby side
     * (see `pim.rb`) and surfaced via `data_model_as_json.assetLayouts`. They
     * share the `PIM::Layout` shape with item layouts, so this returns the
     * same `DataModelLayout` type. Unlike `layout()`, no category-to-layout
     * mapping applies — asset layouts are looked up by their declared name.
     */
    assetLayout: (layoutName: string) => DataModelLayout | undefined;

    hasAssetLayout: (layoutName: string) => boolean;

    attribute: (attributeName: string) => DataModelAttribute | null;

    /**
     * Returns a list of layout sections for the specified layout where only
     * those attributes are retained which belong to the specified item's category.
     */
    filteredSections: (layout: DataModelLayout, item: Item, additionalSectionAttributes: any) => LayoutSection[];

    /**
     * Returns parameters defined for the given attribute in the given section.
     */
    sectionAttributeParam: (layoutName: string, item: Item, sectionName: string, attributeName: string, param: string) => unknown;

    getDefaultItem: (category: string, orgId?:string, userId?: number) => Item | undefined;

    setDefaultItem: (category: string, item: Item, orgId?: string, userId?:number) => void;

    /**
     * Returns parameters defined for the given attribute in the given section.
     * If the `sectionName` is not provided, it returns the attribute param from any section in the layout.
     * This is helpful in places where we don't have actual sections but we have layouts (ex. mass update).
     */
    anySectionAttributeParam: (layoutName: string, item: Item, sectionName: string | null, attributeName: string, param: string) => unknown;

    /**
     * Returns the option list object defined in the data model map.
     */
    optionList: (optionListName: string) => DataModelOptionList;

    /**
     *
     * Returns a promise of loading the intended options whether from the cached storage or the DataModelResource.
     */
    optionListOptionsAsync: (optionListName: string, groupNames: string[]) => Promise<DataModelAttributeOption[]>;

    /**
     * Returns the attribute that is referenced by the given attribute.
     */
    getReferencedOptionAttribute: (attribute: DataModelAttribute) => DataModelAttribute | null;

    /**
     *
    */
    filteredLayoutAttributes: (layoutName: string, item?: Item)=> DataModelAttribute[];

    /**
     * Returns true if the attribute is potential Primary Key
    */
    potentialPrimaryKeyPart: (attributeName: string) => boolean;

    /**
     * Returns true if the attribute is meta attribute
    */
    isMetaAttribute: (attributeName: string) => boolean;

    /**
     * Returns a map of the used additional categories in the item where the key is `EXTENSION:ADDITIONAL_MODULE`
     * and the value are the used values of these additional modules.
     *
     * example:
     * {
     *      'EXTENSION::AdditionalDemoModel': ['BB3']
     * }
     */
    getItemAdditionalCategories: (item: Item) => Record<string, string[]>;

    /**
     * Returns a filtered list of additional categories per additional module which are not yet cached/loaded in
     * the data model object.
     */
    getMissingAdditionalCategories: (additionalCategoriesPerModule: Record<string, string[]>) => Record<string, string[]>;

    /**
     * Adds the additional categories of additional modules into the data model map of cached additional categories.
     */
    addAdditionalCategories: (additionalCategoriesPerModule: Record<string, AdditionalCategory[]>) => void;

    /**
     * Returns the names of attributes for each given additional module, if any.
     */
    getMissingAdditionalAttributes: (additionalCategoriesPerModule: Record<string, string[]>) => Record<string, string[]>;

    /**
     * Adds the additional categories of additional modules attributes into the data model map of cached additional attributes.
     */
    addAdditionalAttributes: (additionalAttributesPerModule: Record<string, DataModelAttribute[]>) => void;

    getReviewAttributes: () => any;

};

export enum DataModelAttributeBaseClass {
    Array = 'Array',
    Date = 'Date',
    DateTime = 'DateTime',
    Dimensional = 'Dimensional',
    Hash = 'Hash',
    Float = 'Float',
    String = 'String',
    Struct = 'Struct',
    Integer = 'Integer',
    Group = 'Group'
}

export enum DataModelAttributeTypeName {
    AdditionalCategory = 'AdditionalCategory',
    Boolean = 'Boolean',
    Codelist = 'Codelist',
    CodelistSet = 'CodelistSet',
    Collection = 'Collection',
    Composite = 'Composite', // <-- Composites are never rendered and only used for their properties
    Date = 'Date',
    DateTime = 'DateTime',
    Decimal = 'Decimal',
    Dimensional = 'Dimensional',
    Document = 'Document',
    Enum = 'Enum',
    EnumSet = 'EnumSet',
    Group = 'Group',
    Image = 'Image',
    Asset = 'Asset',
    MultiDimensional = 'MultiDimensional',
    MultiReference = 'MultiReference',
    OpenCodelist = 'OpenCodelist',
    OpenCodelistSet = 'OpenCodelistSet',
    OpenEnum = 'OpenEnum',
    OpenEnumSet = 'OpenEnumSet',
    Password = 'Password',
    Physical = 'Physical',
    SingleReference = 'SingleReference',
    String = 'String',
    TextArea = 'TextArea',
    MultiLineDimensional = 'MultiLineDimensional'
}

export enum DataModelAttributeRenderer {
    HTMLDimensional = 'HTMLDimensional',
    HTMLInput = 'HTMLInput',
    SingleBoolean = 'SingleBoolean',
}

export interface UiCollectionSorterOption {
    sortingAlgorithm?: string;
    suppressRemoveSort?: boolean;
    sortPriority?: number;
    initialDirection?: 'asc' | 'desc';
    sortDirectionCycle?: ('asc' | 'desc' | null)[];
}

/**
 * Hash keyed by member attribute name, or the SyncManager list form `[{ key, value }, …]`.
 */
export type UiCollectionSorter =
    | Record<string, UiCollectionSorterOption>
    | Array<{ key: string; value: UiCollectionSorterOption }>;

export interface DataModelAttribute {
    optionsFilter?: string | undefined;
    primaryKey__: string;
    name: string;
    label?: string;
    description?: string;
    optionList?: any;
    options?: any;
    translatedDescription?: string;
    translatedLabel?: string;
    baseClass: DataModelAttributeBaseClass;
    typeName?: DataModelAttributeTypeName;
    model_defined: boolean;
    members?: string[];
    keyMembers?: string[];
    valueMembers?: string[];
    item?: any;
    readonly?: boolean;
    mappedFromAsset?: boolean;
    additionalParams?: {
        readonly?: boolean;
        hidden?: boolean;
        collectionRowPath?: string;
    };

    params?: {
        additionalCategory?: string;
        additionalModule?: string;
        alternativeAttribute?: any;
        categoryLevel?: any;
        checkUrl?: any;
        urlCompanionAttribute?: string;
        codelist?: any;
        confidential?: boolean;
        custom?: Record<never, never>,
        customizable?: boolean;
        customRenderer?: string | { [key:string]: string | number; }[];
        defaultValue?: any;
        description?: string;
        dimensionXmlKey?: any;
        disableHistory?: boolean;
        disableSearchIndex?: boolean;
        disableValueNormalization?: boolean;
        dynamicValuesUrl?: string;
        exactSearch?: any;
        extension?: string;
        filter?: string | string[];
        formatter?: string;
        hasChecksum?: boolean;
        isMandatory?: boolean;
        isMultiValue?: boolean;
        keyAttribute?: string;
        keyClass?: any;
        keys?: [string] | string;
        label?: string;
        length?: [number];
        maxPrecision?: any;
        maxScale?: any;
        maxValue_excluding?: any;
        maxValue?: any;
        minValue_excluding?: any;
        minValue?: any;
        onlyLeafCategory?: any;
        parser?: string;
        potentialPrimaryKeyPart?: boolean;
        range?: any;
        readonly?: boolean;
        disableDragAndDrop?:boolean;
        hideAssetFolderBtn?:boolean;
        referenceableCategories?: any;
        references?: any;
        regexp?: string;
        renderer?: any;
        toXml?: boolean;
        uiCodelistFilter?: string;
        uiCollectionFilter?: string;
        /**
         * SyncManager serializes this param as `[{ key, value }, …]`.
         * Stories and tests also pass a hash keyed by member attribute name.
         */
        uiCollectionSorter?: UiCollectionSorter;
        uiGroupFilter?: string;
        uiGroupTitleFilter?: any;
        uiOptionsFilter?: string;
        uiReferenceFilter?: string;
        uiSearchQuery?: string;
        uiShowLength?: boolean;
        valueAttribute?: string;
        valueClass?: any;
        values?: {
            key: string;
            value: string;
        }[] | string;
        xmlAddAttributes?: boolean;
        xmlAddParents?: boolean;
        xmlForce?: boolean;
        xmlIgnore?: boolean;
        xmlKey?: string;
        xmlName?: string;
        min?: number;
        max?: number;
        //New params for the reverse multiReference attribute
        referenceCategories?: string[];
        referenceItemAttributes?:string[];
        hierarchyName?: string;
        reverseHierarchy?: boolean;
    }
}

export interface DataModelAttributeOption {
    key: string;
    value: string;
    translatedOption?: string;
    disabled?: boolean;

    // In case of option lists
    groups?: string[];
    icon?: string;
    icons?: string;
    label?: string;
    labels?: string[];
    name?: string;
    serviceName?: string;
}

export interface Item {
    // FIXME: properly type the Item
    [key: string]: any;
    attributeStates__: string[];
    category__?: string;
    gln? : string
}

export interface PersistedItem extends Item {
    primaryKey__: string;
    __validations__?: Record<string, BaseValidation[]>;
    validation__?: string[];
    validation_warnings__?: string[];
    validation_dirty__: boolean;
    channel__: string;
    supplier__: string;
    published__: string;
}

export interface DataModelLayout {
    name: string;
    sections: LayoutSection[];
    model_defined: boolean;
    layoutOptions?: LayoutOptions;
}

export interface LayoutSection {
    name: string;
    label: string | null;
    attributes: LayoutSectionAttribute[];
    model_defined: boolean;
}

interface LayoutSectionAttribute extends DataModelAttribute {
    hidden: boolean;
}

/**
 * Configuration for a single attribute in secondary_item_title_filter array mode.
 * Similar to how valuesFormat works for Enum/EnumSet in editor layout definition.
 */
export interface SecondaryTitleAttributeConfig {
    /** Attribute name from the data model */
    name: string;
    /**
     * Format parts to display. Default: ['label', 'key']
     * Allowed values: 'icon', 'label', 'key'
     * Only Enum/EnumSet/OpenEnum/OpenEnumSet support 'icon'.
     * Physical and Dimensional types are not allowed.
     */
    valuesFormat?: ('icon' | 'label' | 'key')[];
}

interface LayoutOptions {
    hideCategorySection: boolean;
    itemTitleFilter: string;
    layoutFilter: string;
    hierarchyNavigation: any;
    /**
     * Secondary item title filter for the editor header.
     * Can be either:
     * - A string: Name of a custom directive filter (like item_title_filter)
     * - An array: Ordered list of attributes to display using LabelValueAttribute
     */
    secondaryItemTitleFilter?: string | SecondaryTitleAttributeConfig[];
}

interface DataModelOptionList {
    name: string;
    options?: DataModelAttributeOption[];
    serviceName: string;
    /** Set when service-backed list has been fetched (AngularJS DataModel + React hydration). */
    optionsLoaded?: boolean;
    /** Present while AngularJS is fetching option list options. */
    optionsLoadingPromise?: unknown;
    /**
     * Legacy group-filter map: groupName → [optionKey, ...]. Consumed by
     * dataModel.js#getGroupFilteredOptions and queries.ts#filterOptionListOptionsByGroups
     * when `keysOptionGroups` is configured on a dimensional attribute.
     */
    groups?: Record<string, string[]>;
}

export type DataModelSortingFunction<Row, Value> = (
    valueA: Value,
    valueB: Value,
    rowA: Row,
    rowB: Row,
    direction: 'asc' | 'desc',
    memberAttribute: DataModelAttribute,
    parentAttribute: DataModelAttribute,
    user: User | undefined,
    organization: Organization | undefined
) => -1 | 0 | 1;

export type DataModelCollectionFilteringFunction<TData> = (
    collection: TData[],
    item: Item,
    attribute: DataModelAttribute,
    user: User | undefined,
    organization: Organization | undefined
) => TData[];

/*
 * Custom filter defined in the data model's customDirectives.
 */
export type DataModelCustomFilter =
    (keys: string[], item: Item, attribute: DataModelAttribute, user: User, organization: Organization, collectionEntry?: Item) => string[];

/*
 * Custom filter defined in the data model's customDirectives for MultiReference and SingleReference attributes.
 */
export type DataModelUiReferenceFilter =
        (referencedPrimaryKe: string, referencedItem: Item, item: Item, attribute: DataModelAttribute, user: User, organization: Organization) => string;

/*
 * Custom filter defined in the data model's customDirectives for constructing the item title.
 */
export type DataModelItemTitleFilter =
        (item: Item, user?: User, organization?: Organization) => string;

/*
 * Custom filter defined in the data model's customDirectives for constructing a group entry title.
 */
export type DataModelGroupTitleFilter =
        (groupEntry: any, entryIndex: number, filterParameters: string[], attribute: DataModelAttribute, item: Item, user: User, organization: Organization) => string;

/*
 * Represents attributes that might be added via a layout filter.
 */
export interface LayoutFilterAttribute {
        name: string,
        readonly?: boolean;
        hidden?: boolean
}
/*
 * Custom filter defined in the data model's customDirectives for mutating or adding additional attributes.
 */
export type DataModelCustomLayoutFilter =
    (layout: DataModelLayout, item: Item, user: User, organization: Organization) => Record<string, (LayoutFilterAttribute | string)[]>;

export interface AdditionalCategory {
    code: string;
    dataModel: string;
    // FIXME: Rename to 'serviceName'
    extension: string;
    // FIXME: Rename to 'name'
    id: string;
    kind: string;
    leaf: boolean;
    name: string;
    parent: string;
    // FIXME: Rename to 'translatedLabel'
    title: string;
};

export interface CachedAdditionalCategory extends AdditionalCategory {
    dataModelHash: string;
    additionalModule: string;
    additionalCategoryNameOrCode: string;
    translatedLanguage?: string;
}

export interface AssetMappingAttributeMapping {
    from: string;
    to: string;
}

export interface AssetMappingTarget {
    attribute: string;
    path?: string[];
}

export interface AssetMappingEntry {
    enumValue: string;
    target: AssetMappingTarget;
    urlTo: string;
    attributeMappings: AssetMappingAttributeMapping[];
}

export interface AssetMappingBlock {
    typeAttribute: string;
    assetCategory: string;
    entries: AssetMappingEntry[];
}

export interface AssetLinkPickerFilter {
    target: AssetMappingTarget;
    typeAttribute: string;
    allowedTypes: string[];
    assetCategory: string;
}

export interface DataModelCategory {
    attributes: string[];
    assetLinkPickerFilters?: Record<string, AssetLinkPickerFilter>;
    assetMappings?: AssetMappingBlock[];
    code?: string;
    hidden: boolean;
    hierarchies?: string[];
    label: string;
    modification_status?: boolean;
    model_defined: boolean;
    name: string;
    parent: string;
};
